import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Paper,
  Button,
  Alert,
  Snackbar,
  Backdrop,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import { LoaderWrapper } from "../../components/index";

// Import API services
import {
  getPaymentDetails,
  getTransactionReport,
  getAccountSummary,
  exportPaymentData,
} from "../../services/api";

// Import all Accounts sections
import PaymentDetails from "./payment/PaymentDetails";
import TransactionReport from "./payment/TransactionReport";
import AccountDashboard from "./payment/AccountDashboard";
import InvoiceManagement from "./payment/InvoiceManagement";
import InvoiceView from "./invoice/InvoiceView";
import AAFeeInvoiceGenerate from "./invoice/AAFeeInvoiceGenerate";
import ApprovalRequest from "./payment/ApprovalRequest";
import ApprovalDetails from "./payment/ApprovalDetails";
import OfflinePaymentDetails from "./payment/OfflinePaymentDetails";
import ProformaInvoice from "./payment/ProformaInvoice";
import GeneratedInvoice from "./payment/GeneratedInvoice";
import GeneratedAafeeInvoice from "./payment/GeneratedAafeeInvoice";
import AafeeProformaInvoice from "./payment/AafeeProformaInvoice";
import OfflinePaymentApproval from "./payment/OfflinePaymentApproval";
import OfflinePaymentForm from "./payment/OfflinePaymentForm";
import UnpaidInvoice from "./payment/UnpaidInvoice";
import UnpaidAssessmentProformaInvoice from "./invoice/UnpaidAssessmentProformaInvoice";
import GSTExemption from "./exemption/GSTExemption";
import SEZExemption from "./exemption/SEZExemption";
import SEZApplications from "./applications/SEZApplications";
import AccountsDownload from "./download/AccountsDownload";
import InvoiceDataPushed from "./payment/InvoiceDataPushed";
import OutstandingLedger from "./payment/OutstandingLedger";

const AccountsLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Navigation menu items
  const menuItems = [
    { 
      path: '/main/accounts/dashboard', 
      component: AccountDashboard,
      title: 'Dashboard',
      description: 'Account overview and statistics'
    },
    { 
      path: '/main/accounts/payment-details', 
      component: PaymentDetails,
      title: 'Payments',
      description: 'Account overview and statistics'
    },
    { 
      path: '/main/accounts/transaction-report', 
      component: TransactionReport,
      title: 'Transaction Report',
      description: 'Generate transaction reports'
    },
    { 
      path: '/main/accounts/invoice-management', 
      component: InvoiceManagement,
      title: 'Invoice Management',
      description: 'Manage invoices and billing'
    },
    { 
      path: '/main/accounts/invoice', 
      component: InvoiceView,
      title: 'Invoice View',
      description: 'View and download invoice'
    },
    { 
      path: '/main/accounts/aafee-invoice-generate', 
      component: AAFeeInvoiceGenerate,
      title: 'AA Fee Invoice Generate',
      description: 'Generate and manage AA Fee invoices'
    },
   
    { 
      path: '/main/accounts/approvalRequest', 
      component: ApprovalRequest,
      title: 'Approval Request',
      description: 'View and manage approval requests'
    },
    { 
      path: '/main/accounts/approval-details', 
      component: ApprovalDetails,
      title: 'Approval Details',
      description: 'View and manage approval requests'
    },
    { 
      path: '/main/accounts/offline-payment-details', 
      component: OfflinePaymentDetails,
      title: 'Offline Payment Details',
      description: 'View and manage offline payment details'
    },
    { 
      path: '/main/accounts/proforma-invoice', 
      component: ProformaInvoice,
      title: 'Proforma Invoice',
      description: 'View Proforma Invoice'
    },
    { 
      path: '/main/accounts/generated-invoice', 
      component: GeneratedInvoice,
      title: 'Generated Invoice',
      description: 'View Generated Invoice'
    },
    { 
      path: '/main/accounts/generatedaafee-invoice', 
      component: GeneratedAafeeInvoice,
      title: 'Generated Aafee Invoice',
      description: 'View Generated Aafee Invoice'
    },
     { 
      path: '/main/accounts/aafeeproforma-invoices', 
      component: AafeeProformaInvoice,
      title: 'AA Fee Proforma Invoice',
      description: 'Generate and manage AA Fee Proforma invoices'
    },
    { 
      path: '/main/accounts/offline-payment-approval', 
      component: OfflinePaymentApproval,
      title: 'Offline Payment Approval',
      description: 'Approve offline payment'
    },
    { 
      path: '/main/accounts/offline-payment-form', 
      component: OfflinePaymentForm,
      title: 'Offline Payment',
      description: 'Create new offline payment'
    },
    {
      path: '/main/accounts/unpaid-invoice',
      component: UnpaidInvoice,
      title: 'Unpaid Invoice',
      description: 'View and manage unpaid invoices'
    },
    // {
    //   path: '/main/accounts/unpaid-assessment-proforma-invoice',
    //   component: UnpaidAssessmentProformaInvoice,
    //   title: 'Assessment Proforma Invoice',
    //   description: 'View assessment proforma invoice'
    // },
    { 
      path: '/main/accounts/gst-exemption', 
      component: GSTExemption,
      title: 'GST Exemption',
      description: 'Manage GST exemption requests'
    },
    { 
      path: '/main/accounts/sez-exemption', 
      component: SEZExemption,
      title: 'SEZ Exemption',
      description: 'Manage SEZ exemption requests'
    },
    { 
      path: '/main/accounts/sez-applications', 
      component: SEZApplications,
      title: 'SEZ Applications',
      description: 'View SEZ applications'
    },
    { 
      path: '/main/accounts/download', 
      component: AccountsDownload,
      title: 'Data Download',
      description: 'Download various accounts reports and data'
    },
    {
      path: '/main/accounts/invoice-data-pushed',
      component: InvoiceDataPushed,
      title: 'Invoice Data Pushed',
      description: 'Download various accounts reports and data'
    },
    {
      path: '/main/accounts/outstanding-ledger',
      component: OutstandingLedger,
      title: 'Outstanding Amount & Ledger',
      description: 'Look up outstanding amounts and ledger entries'
    },
  ];

  const currentMenuItem = useMemo(() => {
    return menuItems.find(item => location.pathname === item.path) || menuItems[0];
  }, [location.pathname, menuItems]);

  const ComponentToRender = currentMenuItem?.component || AccountDashboard;

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    // Set page title based on current route
    document.title = `Accounts - ${currentMenuItem?.title || 'Dashboard'} | NABL`;
  }, [currentMenuItem]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleCollapseToggle = () => {
    setCollapsed(!collapsed);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const drawerWidth = collapsed ? 80 : 280;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 2,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
            }
          }}
          onClick={handleDrawerToggle}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerOpen ? drawerWidth : 0,
          flexShrink: 0,
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={handleDrawerToggle}
          collapsed={collapsed}
          onCollapseToggle={handleCollapseToggle}
          currentPath={location.pathname}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
          overflow: 'auto',
          ml: isMobile ? 0 : (drawerOpen ? 0 : `-${drawerWidth}px`),
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            py: 3,
            mt: isMobile ? 8 : 5,
            px: { xs: 2, sm: 3 },
            ml: isMobile ? 0 : 0,
          }}
        >
        

          {/* Component Content */}
          <Box sx={{ position: 'relative' }}>
            <LoaderWrapper loading={loading}>
              <ComponentToRender 
                showSnackbar={showSnackbar}
                setLoading={setLoading}
                loading={loading}
              />
            </LoaderWrapper>
          </Box>
        </Container>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading...
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default AccountsLayout;